/**
 * Auth service — thin wrappers over the typed authApi functions.
 * Kept as a service layer so screens/context can import from a single path.
 */
import { getDeviceTypeCode } from '../../platform/device';
import authApi from '../auth/authApi';
import { createInvalidOtpError } from '../auth/authErrors';
import type { LoginRequest } from '../auth/authTypes';

import { refreshAccessToken } from '../auth/tokenRefresh';

import type { ForgotPasswordPayload, ResetPasswordPayload, VerifyOtpPayload } from '../../types';

const authService = {
  /** POST /api/v1/Auth/Login */
  login: (payload: LoginRequest) =>
    authApi.login({
      ...payload,
      deviceType: getDeviceTypeCode(),
    }),

  /** POST /api/v1/Auth/RevokeToken — call on logout */
  logout: authApi.revokeToken,

  /** POST /api/v1/Auth/ForgotPassword */
  forgotPassword: (payload: ForgotPasswordPayload) =>
    authApi.forgotPassword({ email: payload.email }),

  /**
   * POST /api/v1/Auth/VerifyPasswordResetOtp
   * The endpoint answers 200 with `{ isValid: false }` for a wrong code, so it
   * is rejected here to keep callers on the usual try/catch path.
   */
  verifyPasswordResetOtp: async (payload: VerifyOtpPayload) => {
    const response = await authApi.verifyPasswordResetOtp({
      otpCode: payload.otpCode,
      passwordResetToken: payload.passwordResetToken,
    });
    // Only the explicit `false` is treated as a rejection so an empty success
    // body never blocks a legitimate code.
    if (response.data?.isValid === false) {
      throw createInvalidOtpError(response.data?.message);
    }
    return response;
  },

  /** GET /api/v1/Auth/ValidatePasswordResetToken */
  validatePasswordResetToken: (token: string) =>
    authApi.validatePasswordResetToken(token),


  /** POST /api/v1/Auth/ResetPassword */
  resetPassword: (payload: ResetPasswordPayload) =>
    authApi.resetPassword({
      email: payload.email ?? '',
      newPassword: payload.newPassword ?? payload.password ?? '',
      otpCode: payload.otpCode ?? '',
      passwordResetToken: payload.passwordResetToken ?? '',
    }),
  refreshAccessToken,
  refreshToken: authApi.refreshToken,
};

export default authService;

